import React, { useContext, useState, useEffect, useMemo } from 'react'
import { makeStyles, tokens, ComboboxProps, Combobox, Option, mergeClasses } from '@fluentui/react-components'
import { Process, UserAccount } from '../../api/schemas/schema'
import { DataContext } from '../../context/DataContext'
import { Text } from '../common/text/Text'
import { Person20Regular } from '@fluentui/react-icons'
import { UseFormReturn } from 'react-hook-form'
import { getFullName } from '../../helpers/accountHelper'
import { Field } from '../common/inputs/Field'

const useStyles = makeStyles({
   processField: {
      position: 'relative',
      gridTemplateColumns: '1fr 1fr',
      columnGap: tokens.spacingHorizontalM,
      '& label': {
         textAlign: 'right',
      },
      '@media screen and (max-width: 1007px)': {
         '& label': {
            textAlign: 'left',
         },
         gridTemplateColumns: '1fr',
      },
   },
   comboBox: {
      '@media screen and (max-width: 1007px)': {
         width: '100%',
      },
   },
   proccessFieldIconPadding: {
      '& input': {
         paddingLeft: tokens.spacingHorizontalXXL,
      },
   },
   flex: {
      display: 'flex',
      alignItems: 'center',
      gap: tokens.spacingHorizontalS,
   },
   icon: {
      position: 'absolute',
      zIndex: 1,
      top: '8px',
      marginLeft: '4px',
      '@media screen and (max-width: 1007px)': {
         top: '48px',
      },
   },
})

type DemandAnalysisProcessOwnerPickerFormProps = {
   demandAssessmentForm: UseFormReturn<any>
   processes: Process[]
   process: string
}

export const DemandAnalysisProcessOwnerPicker = ({ demandAssessmentForm, processes, process }: DemandAnalysisProcessOwnerPickerFormProps) => {
   const { setValue, watch } = demandAssessmentForm
   const dataContext = useContext(DataContext)
   const { userAccounts } = dataContext.state
   const classes = useStyles()
   const [matchingOptions, setMatchingOptions] = useState<UserAccount[]>(userAccounts)
   const demandAnalysisProcessOwners = watch('demandAnalysisProcessOwners') ?? []
   const demandAnalysisProcessOwner = demandAnalysisProcessOwners.find((po: any) => po.process.text === process)
   const processObj = useMemo(() => processes.find((x) => x.text === process), [process, processes])
   const disabled = demandAssessmentForm.formState.disabled

   const [input, setInput] = useState<string>('')
   const onInput: ComboboxProps['onChange'] = (event) => {
      const value = event.target.value
      setValue(
         'demandAnalysisProcessOwners',
         demandAnalysisProcessOwners.map((x: any) =>
            x.process.text === process
               ? {
                    ...x,
                    processId: processObj.id,
                    process: processObj,
                    userAccountId: null,
                    userAccount: null,
                    ownerFreeText: value,
                 }
               : x
         ),
         { shouldDirty: true }
      )
      setInput(value)
      let matches = userAccounts
      if (value.length > 0) {
         matches = userAccounts.filter((userAccount) => getFullName(userAccount).toLowerCase().indexOf(value.toLowerCase()) === 0)
      }
      setMatchingOptions(matches)
   }

   useEffect(() => {
      if (demandAnalysisProcessOwner) {
         if (demandAnalysisProcessOwner.userAccount) {
            setInput(getFullName(demandAnalysisProcessOwner.userAccount))
         } else if (demandAnalysisProcessOwner.ownerFreeText) {
            setInput(demandAnalysisProcessOwner.ownerFreeText)
         }
      }
   }, [demandAnalysisProcessOwner])

   return (
      <Field
         label={process}
         className={mergeClasses(
            classes.processField,
            demandAnalysisProcessOwner && demandAnalysisProcessOwner.userAccount ? classes.proccessFieldIconPadding : ''
         )}
         orientation="horizontal"
      >
         <div>
            {demandAnalysisProcessOwner && demandAnalysisProcessOwner.userAccount ? <Person20Regular className={classes.icon} /> : null}
            <Combobox
               disabled={disabled}
               key={process}
               className={classes.comboBox}
               value={input}
               freeform
               onChange={onInput}
               onOptionSelect={(e, data) => {
                  const demandAnalysisProcessOwners = watch('demandAnalysisProcessOwners') ?? []
                  const userAccount = userAccounts.find((ua) => ua.id === data.optionValue)

                  setValue(
                     'demandAnalysisProcessOwners',
                     demandAnalysisProcessOwners.map((x: any) =>
                        x.process.text === process
                           ? {
                                ...x,
                                processId: processObj.id,
                                process: processObj,
                                userAccountId: data.optionValue,
                                userAccount,
                                ownerFreeText: null,
                             }
                           : x
                     ),
                     { shouldDirty: true }
                  )
                  setInput(getFullName(userAccount))
               }}
            >
               {matchingOptions.map((account) => (
                  <Option key={account.id} value={account.id} text={getFullName(account)}>
                     <span className={classes.flex}>
                        <Person20Regular />
                        <Text>{getFullName(account)}</Text>
                     </span>
                  </Option>
               ))}
            </Combobox>
         </div>
      </Field>
   )
}
